/**
 * WebChat API - Reports-focused chat assistant API utilities
 *
 * This module provides API functions for interacting with the webChat
 * service, which is focused exclusively on helping users understand
 * and analyze their reports.
 */

import { API_BASE } from "./api";

const buildAuthHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

function fallbackSessionTitle(session = {}) {
  const preview = normalizeText(session?.preview);
  if (preview) return preview;
  return "New conversation";
}

function normalizeSessionSummary(session = {}) {
  return {
    sessionId: String(session?.sessionId || ""),
    reportId: session?.reportId || null,
    title: normalizeText(session?.title) || fallbackSessionTitle(session),
    preview: normalizeText(session?.preview),
    messageCount: Number(session?.messageCount || 0),
    startedAt: session?.startedAt || null,
    lastActivityAt: session?.lastActivityAt || null,
    lastMessageAt: session?.lastMessageAt || session?.lastActivityAt || null,
  };
}

export function createWebChatSessionId(userId) {
  const baseUserId = String(userId || "").trim();
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const random = Math.random().toString(36).slice(2, 8);
  return `web_${baseUserId}_${timestamp}_${random}`;
}

/**
 * Send a message to the reports chat assistant.
 *
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {Object} params.message - Message object with content and optional attachments
 * @param {string} [params.sessionId] - Optional session ID (will be auto-generated if not provided)
 * @param {string} [params.reportId] - Optional report ID to focus the conversation on
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing sessionId, humanMessages, aiMessages, and optional limit info
 */
export async function sendWebChatMessage({
  userId,
  message,
  sessionId,
  reportId,
  authToken,
}) {
  const res = await fetch(`${API_BASE}/webchat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({
      user_id: userId,
      sessionId,
      reportId,
      message,
    }),
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /webchat JSON: "${text}"`);
  }
}

/**
 * Get chat history for a session.
 *
 * @param {Object} params
 * @param {string} params.sessionId - Session ID
 * @param {string} params.userId - User ID
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing sessionId, reportId, and messages array
 */
export async function getWebChatHistory({ sessionId, userId, authToken }) {
  const url = new URL(`${API_BASE}/webchat/history`);
  url.searchParams.set("sessionId", sessionId);
  url.searchParams.set("userId", userId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat History API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /webchat/history JSON: "${text}"`);
  }
}

/**
 * Get AI messages for a session.
 *
 * @param {Object} params
 * @param {string} params.sessionId - Session ID
 * @param {number} [params.limit] - Optional limit on number of messages
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing sessionId and aiMessages array
 */
export async function getWebChatAiHistory({ sessionId, limit, authToken }) {
  const url = new URL(`${API_BASE}/webchat/aiHistory`);
  url.searchParams.set("sessionId", sessionId);
  if (limit) {
    url.searchParams.set("limit", String(limit));
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat AI History API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /webchat/aiHistory JSON: "${text}"`);
  }
}

/**
 * Get human messages for a session.
 *
 * @param {Object} params
 * @param {string} params.sessionId - Session ID
 * @param {number} [params.limit] - Optional limit on number of messages
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing sessionId and humanMessages array
 */
export async function getWebChatHumanHistory({ sessionId, limit, authToken }) {
  const url = new URL(`${API_BASE}/webchat/humanHistory`);
  url.searchParams.set("sessionId", sessionId);
  if (limit) {
    url.searchParams.set("limit", String(limit));
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat Human History API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /webchat/humanHistory JSON: "${text}"`);
  }
}

/**
 * Get all chat sessions for a user.
 *
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {number} [params.limit] - Optional limit on number of sessions (default: 20)
 * @param {number} [params.skip] - Optional number of sessions to skip for pagination
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing userId and sessions array
 */
export async function getWebChatSessions({ userId, limit, skip, authToken }) {
  const url = new URL(`${API_BASE}/webchat/sessions`);
  url.searchParams.set("userId", userId);
  if (limit) {
    url.searchParams.set("limit", String(limit));
  }
  if (skip) {
    url.searchParams.set("skip", String(skip));
  }

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat Sessions API error ${res.status}: ${text}`);
  }

  try {
    const parsed = JSON.parse(text);
    return {
      ...parsed,
      sessions: Array.isArray(parsed?.sessions)
        ? parsed.sessions.map(normalizeSessionSummary).filter((session) => session.sessionId)
        : [],
    };
  } catch {
    throw new Error(`Failed to parse /webchat/sessions JSON: "${text}"`);
  }
}

export async function getWebChatSessionSummaries(params) {
  return getWebChatSessions(params);
}

/**
 * Get message limits status for a user.
 *
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing userId, limit, currentCount, remaining, and allowed status
 */
export async function getWebChatLimits({ authToken }) {
  const url = new URL(`${API_BASE}/webchat/limits`);
  //url.searchParams.set("userId", userId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`WebChat Limits API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse /webchat/limits JSON: "${text}"`);
  }
}

/**
 * Helper to create a new chat session for a specific report.
 *
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} params.reportId - Report ID to focus the conversation on
 * @param {string} [params.initialMessage] - Optional initial message to send
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response from sendWebChatMessage or session info
 */
export async function startReportChat({ userId, reportId, initialMessage, authToken }) {
  if (initialMessage) {
    return sendWebChatMessage({
      userId,
      message: { content: initialMessage },
      reportId,
      authToken,
    });
  }

  // If no initial message, just return info about limits
  const limits = await getWebChatLimits({ authToken });
  return {
    reportId,
    limits,
  };
}

/**
 * Upload an attachment for the chat.
 *
 * @param {Object} params
 * @param {File} params.file - The file to upload
 * @param {string} [params.authToken] - Optional auth token
 * @returns {Promise<Object>} Response containing url, key, bucket, contentType, size, name
 */
export async function uploadChatAttachment({ file, authToken }) {
  if (!file) {
    throw new Error("File is required for upload");
  }

  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/api/upload/chat-attachment`, {
    method: "POST",
    headers: {
      ...buildAuthHeaders(authToken),
    },
    body: formData,
  });

  const text = await res.text();

  if (!res.ok) {
    throw new Error(`Upload API error ${res.status}: ${text}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse upload response: "${text}"`);
  }
}

/**
 * Upload multiple attachments for the chat.
 *
 * @param {Object} params
 * @param {File[]} params.files - Array of files to upload
 * @param {string} [params.authToken] - Optional auth token
 * @param {function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise<Object[]>} Array of upload responses
 */
export async function uploadChatAttachments({ files, authToken, onProgress }) {
  if (!files || !files.length) {
    return [];
  }

  const results = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    try {
      const result = await uploadChatAttachment({ file, authToken });
      results.push(result);
      if (onProgress) {
        onProgress({ uploaded: i + 1, total: files.length, file: result });
      }
    } catch (err) {
      console.error(`[webchat] failed to upload file ${file.name}`, err);
      results.push({ error: err.message, name: file.name });
    }
  }

  return results;
}
